/**
 * The Antares chart-packs list: browse regional packs and, per pack, Download
 * (save for offline — the primary, reliable path on the app) or View online
 * (stream when connected — a secondary option). Reused in two places so the
 * actions are identical: the "More charts" tab, and inline under the Antares row
 * on the provider-selection screen.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { useChartPacksStore } from '@/store/chartPacks';
import { useNetworkStore } from '@/store/network';
import { useShellStore } from '@/store/shell';
import { useRoutingStore } from '@/store/routing';
import { canDownload, type ChartPack } from '@/chart-sources';
import { isVectorPack } from '@/chart-sources/providers';
import { hasPrivatePackHost } from '@/chart-sources/packsConfig';
import { getChartAccessToken, setChartAccessToken } from '@/chart-sources/chartAccess';
import { haptic } from '@/ui/haptics';

/** Bytes as a rounded MB string for the pack size. */
function mb(bytes: number): string {
  return `${Math.max(1, Math.round(bytes / 1_000_000))} MB`;
}

/** Bytes as a one-decimal MB number (no unit), for live download progress. */
function mbFrac(bytes: number): string {
  return (bytes / 1_000_000).toFixed(1);
}

/** De-emphasised secondary action (View online sits under the primary Download). */
const secondaryBtnStyle: CSSProperties = {
  fontSize: '0.82em',
  fontWeight: 400,
  opacity: 0.8,
  padding: '4px 8px',
};

function PackRow({ pack }: { pack: ChartPack }) {
  const rt = useChartPacksStore((s) => s.runtime[pack.id]) ?? {
    status: 'idle' as const,
    progress: 0,
    bytesDownloaded: 0,
    bytesTotal: 0,
    error: null,
  };
  const viewOnline = useChartPacksStore((s) => s.viewOnline);
  const turnOff = useChartPacksStore((s) => s.turnOff);
  const download = useChartPacksStore((s) => s.download);
  const remove = useChartPacksStore((s) => s.remove);
  const online = useNetworkStore((s) => s.online);

  const downloading = rt.status === 'downloading';
  const downloaded = rt.status === 'downloaded';
  const onlineActive = rt.status === 'online';
  const pct = Math.round(rt.progress * 100);
  // Live byte read from the native download event; only trust it once we have a
  // pack total to divide against, otherwise just show the percentage.
  const bytesLine =
    rt.bytesTotal > 0 ? `${mbFrac(rt.bytesDownloaded)} / ${mbFrac(rt.bytesTotal)} MB · ${pct}%` : `${pct}%`;

  const viewOnlineBtn = (
    <button
      type="button"
      className={`acp-btn${onlineActive ? ' on' : ''}`}
      style={onlineActive ? undefined : secondaryBtnStyle}
      disabled={downloading || (!onlineActive && !online)}
      onClick={() => {
        haptic('tick');
        if (onlineActive) turnOff(pack);
        else void viewOnline(pack);
      }}
      title={!online && !onlineActive ? 'No connection' : undefined}
    >
      {onlineActive ? 'Stop online' : 'View online'}
    </button>
  );

  return (
    <div className={`acp-prov-row${downloaded || onlineActive ? ' on' : ''}`}>
      <div className="acp-prov-main acp-prov-static">
        <b>{pack.region}</b>
        <span>
          {isVectorPack(pack)
            ? `${pack.cellCount ?? pack.chartCount} cells`
            : `${pack.chartCount} charts`}{' '}
          · {mb(pack.sizeBytes)}
          {downloaded ? ' · saved offline' : onlineActive ? ' · viewing online' : ''}
        </span>
        {/* A downloaded pack that has fallen behind says so. Corrections are the
            whole point of a chart subscription, so a silent stale pack would be
            the worst outcome: current-looking charts that are not. */}
        {downloaded && rt.behind > 0 && (
          <span style={{ color: '#e0a020', fontSize: '0.82em' }}>
            {rt.behind === 1 ? '1 chart corrected' : `${rt.behind} charts corrected`} since you
            downloaded this
          </span>
        )}
        {rt.error && <span style={{ color: '#e06666', fontSize: '0.82em' }}>{rt.error}</span>}
        {downloading && <span style={{ fontSize: '0.82em' }}>Downloading… {bytesLine}</span>}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'stretch' }}>
        {/* Download is the primary, reliable path, so it sits on top. Both kinds
            of pack save the same way: raster packs store their tiles, vector
            packs store their cells. */}
        {canDownload() ? (
          downloaded ? (
            <>
              {/* Updating re-fetches only what has changed, so it is quick even
                  on a large pack. */}
              {rt.behind > 0 && (
                <button
                  type="button"
                  className="acp-btn"
                  disabled={downloading || !online}
                  onClick={() => { haptic('tick'); void download(pack); }}
                >
                  {downloading ? bytesLine : 'Update charts'}
                </button>
              )}
              <button type="button" className="acp-btn" onClick={() => { haptic('tick'); void remove(pack); }}>
                Delete
              </button>
            </>
          ) : (
            <button
              type="button"
              className="acp-btn"
              disabled={downloading || !online}
              onClick={() => { haptic('tick'); void download(pack); }}
            >
              {downloading ? bytesLine : `Download (${mb(pack.sizeBytes)})`}
            </button>
          )
        ) : (
          <span style={{ fontSize: '0.78em', opacity: 0.7, textAlign: 'center' }}>Download in the app</span>
        )}
        {/* View online is the secondary, de-emphasised option; hidden once saved. */}
        {!downloaded && viewOnlineBtn}
      </div>
    </div>
  );
}

/**
 * The access-token entry for the private (licensed) pack host. Only rendered
 * when the build has a private host configured. Mirrors the AIS key input:
 * local buffer for smooth typing, hidden by default, committed on blur — and a
 * commit refetches the pack list, because a fresh token is exactly the moment
 * the gated packs should appear.
 */
function AccessTokenRow() {
  const loadManifest = useChartPacksStore((s) => s.loadManifest);
  const [text, setText] = useState(getChartAccessToken());
  const [show, setShow] = useState(false);
  const commit = () => {
    const trimmed = text.trim();
    if (trimmed !== text) setText(trimmed);
    if (trimmed !== getChartAccessToken()) {
      setChartAccessToken(trimmed);
      void loadManifest();
    }
  };
  return (
    <>
      <div className="acp-prov-row" style={{ gap: 6 }}>
        <input
          // The type stays 'text' and the masking is done in CSS. Swapping the
          // type attribute between password and text is the obvious way to do a
          // reveal toggle, and on Android it tears the input down and rebuilds
          // it, which drops focus and shuts the keyboard: tap Show to check
          // what you typed and the keyboard goes away mid-token. -webkit-text-
          // security masks the same way without touching the type.
          type="text"
          value={text}
          placeholder="Chart access token"
          autoCapitalize="off"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          style={{
            flex: 1,
            minWidth: 0,
            WebkitTextSecurity: show ? 'none' : 'disc',
          } as CSSProperties}
        />
        <button type="button" className="acp-btn" onClick={() => setShow((s) => !s)}>
          {show ? 'Hide' : 'Show'}
        </button>
        <button
          type="button"
          className="acp-btn"
          disabled={text === '' && getChartAccessToken() === ''}
          onClick={() => {
            haptic('tick');
            setText('');
            setChartAccessToken('');
            void loadManifest();
          }}
        >
          Clear
        </button>
      </div>
      <p className="acp-menu-note" style={{ marginTop: 2 }}>
        Licensed packs need an access token. Paste it here once; it stays on this device.
      </p>
    </>
  );
}

/**
 * The packs list. `heading` shows the section title + intro (used in the More
 * charts tab); pass false for the compact inline use under the Antares row.
 */
export function ChartPacksPanel({ heading = true }: { heading?: boolean }) {
  const packs = useChartPacksStore((s) => s.packs);
  const status = useChartPacksStore((s) => s.manifestStatus);
  const loadManifest = useChartPacksStore((s) => s.loadManifest);

  // Opening the packs screen kicks off the first load if we've never tried. We do
  // NOT auto-retry on 'error' here — that would loop error→loading→error forever
  // (a perpetual spinner). Recovery is via the Retry button and the online-regain
  // flush instead.
  useEffect(() => {
    if (status === 'idle') void loadManifest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasPacks = packs.length > 0;
  const mode = useChartPacksStore((s) => s.mode);
  const setMode = useChartPacksStore((s) => s.setMode);
  const armAreaSelect = useChartPacksStore((s) => s.armAreaSelect);
  const stageAlongRoute = useChartPacksStore((s) => s.stageAlongRoute);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);
  const routes = useRoutingStore((s) => s.routes);
  const followRouteId = useRoutingStore((s) => s.followRouteId);
  const activeRouteId = useRoutingStore((s) => s.activeRouteId);
  const [routeWidthNm, setRouteWidthNm] = useState(5);
  // Areas are the interface; the per-pack list is the engine room, kept behind
  // a toggle for the day a single pack needs deleting or updating by name.
  const [showAll, setShowAll] = useState(false);
  const route = routes.find((r) => r.id === (followRouteId ?? activeRouteId)) ?? null;

  const drawArea = () => {
    haptic('tick');
    armAreaSelect();
    closeSheet();
    showToast('Drag a box over the water you want offline');
  };
  const alongRoute = () => {
    if (!route || route.waypoints.length < 2) {
      showToast('Pick or follow a route first');
      return;
    }
    haptic('tick');
    stageAlongRoute(
      route.waypoints.map((w) => w.position),
      routeWidthNm,
    );
    // The confirm card lives on the chart; get the menu out of its way.
    closeSheet();
  };

  return (
    <>
      {heading && <div className="rgn">Antares chart packs</div>}
      <span className="lead">
        Pick the water, not the pack: draw a box on the chart, or take everything within a few
        miles of the route. What you download works with no signal at all, which is the mode to
        trust afloat; Online streams whatever the view needs while you have a connection.
      </span>

      <label className="acp-fieldlabel">Mode</label>
      <div className="acp-seg">
        <button
          type="button"
          className={mode === 'online' ? 'on' : ''}
          onClick={() => { haptic('tick'); setMode('online'); }}
          title="Stream the charts the view needs, while there is signal"
        >
          Online
        </button>
        <button
          type="button"
          className={mode === 'downloaded' ? 'on' : ''}
          onClick={() => { haptic('tick'); setMode('downloaded'); }}
          title="Your downloads are the chart; online only fills in beyond their edge, with a notice"
        >
          Downloaded first
        </button>
      </div>
      <p className="acp-menu-note">
        Downloaded first is the seagoing setting: what you saved always draws, and if the view
        strays beyond it the app streams the rest only while there is signal, telling you each
        time you cross the edge, because each crossing is a fresh navigational fact.
      </p>

      <label className="acp-fieldlabel">Get charts for…</label>
      <div className="picks">
        <button type="button" className="acp-btn" onClick={drawArea}>
          Draw an area
        </button>
        <button type="button" className="acp-btn" disabled={!route || route.waypoints.length < 2} onClick={alongRoute}>
          Along the route
        </button>
      </div>
      <div className="acp-stepper" role="radiogroup" aria-label="Route corridor width">
        {([2, 5, 10] as const).map((w) => (
          <button
            key={w}
            type="button"
            className={`acp-btn${routeWidthNm === w ? ' on' : ''}`}
            onClick={() => setRouteWidthNm(w)}
            aria-label={`${w} miles each side of the route`}
          >
            {w} nm
          </button>
        ))}
      </div>
      <p className="acp-menu-note">
        Along the route takes every area the route passes through or comes within that distance
        of, so the charts aboard match the passage rather than a rectangle that happens to hold
        it.
      </p>

      {hasPrivatePackHost() && <AccessTokenRow />}

      <button
        type="button"
        className="acp-btn full"
        aria-expanded={showAll}
        onClick={() => setShowAll((v) => !v)}
      >
        {showAll ? 'Hide the area list' : `All areas (${packs.length})`}
      </button>

      {showAll && hasPacks && packs.map((pack) => <PackRow key={pack.id} pack={pack} />)}

      {!hasPacks && status === 'loading' && (
        <span className="lead" style={{ opacity: 0.75 }}>Loading chart packs…</span>
      )}
      {!hasPacks && status === 'error' && (
        <div className="acp-prov-row">
          <div className="acp-prov-main acp-prov-static">
            <b>Couldn’t load the chart packs</b>
            <span>Check your connection and try again.</span>
          </div>
          <button type="button" className="acp-btn" onClick={() => { haptic('tick'); void loadManifest(); }}>
            Retry
          </button>
        </div>
      )}
      {!hasPacks && status === 'ready' && (
        <span className="lead" style={{ opacity: 0.75 }}>
          No chart packs are published yet. They’ll appear here once they are.
        </span>
      )}
    </>
  );
}
